export type Environment = 'admin' | 'student';

export interface EnvironmentConfig {
  mode: Environment;
  showOccupantDetails: boolean;
  showPopups: boolean;
  allowRegistration: boolean;
  showTestStudentToggle: boolean;
  showChairNumbers: boolean;
}

export const ENVIRONMENT_CONFIGS: Record<Environment, EnvironmentConfig> = {
  admin: {
    mode: 'admin',
    showOccupantDetails: true,
    showPopups: true,
    allowRegistration: true,
    showTestStudentToggle: true,
    showChairNumbers: true,
  },
  student: {
    mode: 'student',
    showOccupantDetails: false,
    showPopups: false,
    allowRegistration: false,
    showTestStudentToggle: false,
    showChairNumbers: true,
  },
};

// Default environment - determined by build-time configuration
export const getDefaultEnvironment = (): Environment => {
  // Check build-time environment variable first (highest priority)
  const buildEnv = process.env.REACT_APP_BUILD_ENVIRONMENT;
  if (buildEnv === 'admin' || buildEnv === 'student') {
    return buildEnv;
  }
  
  // Check runtime environment variable
  const envVar = process.env.REACT_APP_ENVIRONMENT;
  if (envVar === 'admin' || envVar === 'student') {
    return envVar;
  }
  
  // Check localStorage (for development/testing)
  const stored = localStorage.getItem('app-environment');
  if (stored === 'admin' || stored === 'student') {
    return stored;
  }
  
  // Default to admin
  return 'admin';
};

export const setEnvironment = (environment: Environment): void => {
  localStorage.setItem('app-environment', environment);
  // No need to reload - the context will handle the update
};

// Development/Production environment utilities
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

// Environment-based logging utility
export const logger = {
  log: (...args: any[]): void => {
    const enableLogging = process.env.REACT_APP_ENABLE_LOGGING === 'true' || isDevelopment();
    if (enableLogging) {
      console.log(...args);
    }
  },
  error: (...args: any[]): void => {
    // Always log errors, but with more detail in development
    if (isDevelopment()) {
      console.error(...args);
    } else {
      console.error(...args);
    }
  },
  warn: (...args: any[]): void => {
    const enableLogging = process.env.REACT_APP_ENABLE_LOGGING === 'true' || isDevelopment();
    if (enableLogging) {
      console.warn(...args);
    }
  },
  debug: (...args: any[]): void => {
    const enableDebug = process.env.REACT_APP_ENABLE_DEBUG === 'true' || isDevelopment();
    if (enableDebug) {
      console.debug(...args);
    }
  },
};
